import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { LogoComponent } from '@theme/widgets/logo.component';
import { AccessInfoDialogComponent } from './access-info-dialog.component';
import { LoginSettingsService } from './login-settings.service';

/**
 * Window of the login pages (Club-Manager-Login-Spec 2): club logo and name, subtitle, the
 * page content and the footer with "Zugang einrichten", version and legal links.
 * Styles: src/styles/cmc/_auth.scss.
 */
@Component({
  selector: 'app-auth-window',
  template: `
    <main class="cmc-auth-window">
      <header class="cmc-auth-window__head">
        @if (settings().clubLogo) {
          <img class="cmc-auth-window__logo" [src]="settings().clubLogo" alt="" width="72" height="72">
        }
        <h1 class="cmc-auth-window__club">{{ settings().clubName }}</h1>
        <p class="cmc-auth-window__subtitle">{{ subtitle() | translate }}</p>
      </header>

      <ng-content />

      <footer class="cmc-auth-window__footer">
        @if (settings().webmaster) {
          <p class="cmc-auth-window__access">
            {{ 'auth.no_access' | translate }}
            <button type="button" class="cmc-auth-link" (click)="openAccessInfo()">
              {{ 'auth.setup_access' | translate }}
            </button>
          </p>
        }
        <div class="cmc-auth-window__meta">
          <span class="cmc-auth-window__brand">
            <app-logo [size]="18" />
            <strong>Club Manager</strong>
            <span class="tabular-nums">v{{ version }}</span>
          </span>
          <span class="cmc-auth-window__legal">
            @if (settings().imprintUrl; as url) {
              <a [href]="url" target="_blank" rel="noopener">{{ 'auth.imprint' | translate }}</a>
            }
            @if (settings().privacyUrl; as url) {
              <a [href]="url" target="_blank" rel="noopener">{{ 'auth.privacy' | translate }}</a>
            }
          </span>
        </div>
      </footer>
    </main>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, LogoComponent],
})
export class AuthWindowComponent {
  private readonly dialog = inject(MatDialog);
  readonly version = inject<string>('VERSION' as any);

  /** Translation key of the subtitle (depends on the state, spec 4). */
  readonly subtitle = input.required<string>();

  readonly settings = toSignal(inject(LoginSettingsService).settings$, {
    initialValue: {
      clubName: '',
      clubLogo: '',
      webmaster: null,
      imprintUrl: null,
      privacyUrl: null,
      resetLinkMinutes: 30,
      lockoutMinutes: 15,
    },
  });

  /** Pop-up "Zugang einrichten" (spec 5); focus returns to the link afterwards (MatDialog). */
  openAccessInfo(): void {
    const webmaster = this.settings().webmaster;
    if (!webmaster) {
      return;
    }
    this.dialog.open(AccessInfoDialogComponent, {
      data: webmaster,
      width: 'var(--cmc-size-auth-window)',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'cmc-auth-dialog-panel',
      backdropClass: 'cmc-auth-backdrop',
      ariaLabelledBy: 'accessInfoTitle',
      autoFocus: 'dialog',
      restoreFocus: true,
    });
  }
}
